import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Play, X, Loader2 } from "lucide-react";
import { loadActive, clearActive, markHidden, accumulateIdleSinceHidden } from "../lib/activeWorkoutPersistence";

/**
 * ActiveWorkoutBanner
 * -------------------
 * Floating "Workout in progress — Continue" pill that appears at the bottom
 * of every protected page (above the BottomNav) whenever the user has an
 * in-progress workout saved to localStorage but is currently looking at
 * something else (Social, Coach Hub, Profile, etc.).
 *
 * Tap it → navigate to /active/{workout_id}, where ActiveWorkout reads the
 * saved snapshot and restores every ticked set, typed weight, and the
 * elapsed timer exactly as it was.
 *
 * Dismiss "X" → confirm + clear the saved state (abandon flow).
 *
 * State source: localStorage via lib/activeWorkoutPersistence + a custom
 * `gymgrizz:active-changed` window event for same-tab reactivity (the
 * native `storage` event only fires across tabs).
 */
export default function ActiveWorkoutBanner() {
  const nav = useNavigate();
  const loc = useLocation();
  const [active, setActive] = useState(() => loadActive());
  const [now, setNow] = useState(() => Date.now());
  const [continuing, setContinuing] = useState(false);

  // Refresh `active` whenever ActiveWorkout (or anything else) writes to
  // the persistence layer. Also re-read on focus / cross-tab `storage`.
  useEffect(() => {
    const refresh = () => setActive(loadActive());
    window.addEventListener("gymgrizz:active-changed", refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("gymgrizz:active-changed", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  // App-wide idle accumulation. When the tab/PWA goes hidden (phone locked,
  // app backgrounded, screen off) we stamp the hide time. When visibility
  // returns we add the elapsed gap to `idleMs` in localStorage so the
  // workout's "active time" excludes it. This runs even when the user is
  // navigated AWAY from /active (e.g. browsing Social and then locking the
  // phone) because this banner is mounted on every protected page.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden") markHidden();
      else accumulateIdleSinceHidden();
    };
    document.addEventListener("visibilitychange", handler);
    window.addEventListener("pagehide", markHidden);
    window.addEventListener("pageshow", accumulateIdleSinceHidden);
    return () => {
      document.removeEventListener("visibilitychange", handler);
      window.removeEventListener("pagehide", markHidden);
      window.removeEventListener("pageshow", accumulateIdleSinceHidden);
    };
  }, []);

  // Live-update the elapsed clock once a second — only ticks while the
  // banner is actually rendered (the `active` early-return below keeps the
  // interval cost effectively zero for users with no in-progress workout).
  useEffect(() => {
    if (!active) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [active]);

  // Don't render on the ActiveWorkout page itself (or login/onboarding).
  const onActivePage = useMemo(
    () => active && loc.pathname === `/active/${active.workout_id}`,
    [active, loc.pathname],
  );

  if (!active || onActivePage) return null;

  const wk = active.wk || {};
  const exercises = wk.exercises || [];
  const totalSets = exercises.reduce((n, e) => n + (e.sets?.length || 0), 0);
  const doneSets = exercises.reduce(
    (n, e) => n + (e.sets?.filter((s) => s.completed).length || 0),
    0,
  );
  const elapsedMs = Math.max(
    0,
    now - (active.startedAt || now) - (active.idleMs || 0),
  );
  const mins = Math.floor(elapsedMs / 60000);
  const secs = Math.floor((elapsedMs % 60000) / 1000);
  const elapsed = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;

  const handleContinue = () => {
    setContinuing(true);
    nav(`/active/${active.workout_id}`);
  };
  const handleDismiss = (e) => {
    e.stopPropagation();
    if (
      window.confirm(
        "Abandon this in-progress workout? All ticked sets and typed weights will be lost.",
      )
    ) {
      clearActive();
    }
  };

  return (
    <div
      data-testid="active-workout-banner"
      onClick={handleContinue}
      className="fixed left-3 right-3 z-40 cursor-pointer bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white border border-blue-400 shadow-lg shadow-blue-500/30 transition-colors"
      style={{
        bottom: "calc(5rem + env(safe-area-inset-bottom) + 0.5rem)",
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center gap-3 p-3">
        <div className="flex-shrink-0 w-9 h-9 bg-white/15 flex items-center justify-center">
          {continuing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold leading-none">
            Workout in progress
          </div>
          <div
            data-testid="active-workout-banner-name"
            className="font-display text-base uppercase truncate leading-tight mt-0.5"
          >
            {wk.name || "Active workout"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/80 mt-0.5">
            {doneSets}/{totalSets} sets · {elapsed}
          </div>
        </div>
        <div
          data-testid="active-workout-banner-continue"
          className="hidden sm:block px-3 py-1.5 bg-white text-blue-600 font-display uppercase tracking-wider text-xs"
        >
          Continue
        </div>
        <button
          data-testid="active-workout-banner-dismiss"
          onClick={handleDismiss}
          aria-label="Abandon workout"
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
